/*
 |--------------------------------------------------------------------------
 | Site Header
 |
 | This module adds functionality to the responsive site header navigation
 |--------------------------------------------------------------------------
 */

// Constants
const ASiteHeaderSelector = '[data-site-header]';
const ASiteHeaderDesktopBreakpoint = 720;
const ASiteHeaderDesktopNavSelector = '[data-site-header-desktop]';
const ASiteHeaderMobileNavSelector = '[data-site-header-mobile]';
const ASiteHeaderMobileOverlayNavSelector =
  '[data-site-header-mobile-overlay-nav]';
const ASiteHeaderHiddenClass = 'a-site-header--hidden';
const ASiteHeaderScrolledUpClass = 'a-site-header--scrolled-up';
const ASiteHeaderToggleSelector = '[data-site-header-mobile-toggle]';
const ASiteHeaderNoScrollClass = 'a-site-header__noScroll';
const ASiteHeaderScrollThreshold = 200;
const ASiteHeaderTargetGroupRadioSelector =
  '[data-site-header-target-group-switcher-radios] input[type="radio"]';
const ASiteHeaderTargetGroupWasActiveAttribute = 'data-was-active';
const ASiteHeaderTargetGroupSwitchedAttribute = 'data-target-group-switched';
const ASiteHeaderTargetGroupAnimationDuration = 300;
const ASiteHeaderSearchToggleSelector = '[data-site-header-search-toggle]';
const ASiteHeaderSearchOpenSelector = '[data-site-header-search-open]';
const ASiteHeaderSearchCloseSelector = '[data-site-header-search-close]';
const ASiteHeaderSearchboxButtonSelector = '#searchbox-button';
const ASiteHeaderCoveoSearchboxClassName = 'site-header__search';
const ASiteHeaderCoveoSearchboxActiveAddition = '--is-active';
const ASiteHeaderOpenPopoverSelector = '.a-popover[open]';

// Class
class ASiteHeader {
  lastScrollY = 0;
  lastCheckedRadioIndex = -1;
  clearWasActiveTimer = null;
  clearTargetGroupSwitchedTimer = null;
  popoverObserver = null;

  init = () => {
    this.header = document.querySelector(ASiteHeaderSelector);
    this.navDesktop = document.querySelector(ASiteHeaderDesktopNavSelector);
    this.navMobile = document.querySelector(ASiteHeaderMobileNavSelector);
    this.navMobileOverlay = document.querySelector(
      ASiteHeaderMobileOverlayNavSelector
    );
    this.toggleButton = document.querySelector(ASiteHeaderToggleSelector);
    this.searchToggleButtons = document.querySelectorAll(
      ASiteHeaderSearchToggleSelector
    );
    this.searchOpenButtons = document.querySelectorAll(
      ASiteHeaderSearchOpenSelector
    );
    this.searchCloseButtons = document.querySelectorAll(
      ASiteHeaderSearchCloseSelector
    );
    this.radios = [
      ...document.querySelectorAll(ASiteHeaderTargetGroupRadioSelector),
    ];
    this.radios.forEach((radio) =>
      radio.removeAttribute(ASiteHeaderTargetGroupWasActiveAttribute)
    );

    this.lastCheckedRadioIndex = this.radios.findIndex(
      (radio) => radio.checked
    );

    this.setupPopoverObserver();
    this.bindEvents();
    this.handleResize();
  };

  bindEvents = () => {
    window.addEventListener('resize', this.handleResize.bind(this));
    window.addEventListener('scroll', this.handleScroll.bind(this), {
      passive: true,
    });
    this.toggleButton?.addEventListener('click', this.toggleMobileNav);
    this.radios.forEach((radio, index) => {
      radio.addEventListener('change', () =>
        this.handleTargetGroupChange(index)
      );
    });

    this.searchToggleButtons.forEach((button) =>
      button.addEventListener('click', this.toggleSearch)
    );
    this.searchOpenButtons.forEach((button) =>
      button.addEventListener('click', this.openSearch)
    );
    this.searchCloseButtons.forEach((button) =>
      button.addEventListener('click', this.closeSearch)
    );

    document.addEventListener('keydown', this.handleSearchboxTabNavigation);
  };

  handleTargetGroupChange = (newIndex) => {
    const previousIndex = this.lastCheckedRadioIndex;
    const selectedTargetGroup = this.radios[newIndex]?.value;

    if (
      typeof selectedTargetGroup === 'string' &&
      selectedTargetGroup.length > 0
    ) {
      fetch(
        `/api/targetgroup/setcookie/${encodeURIComponent(selectedTargetGroup)}`,
        {
          method: 'POST',
        }
      );
    }

    if (previousIndex >= 0 && this.header) {
      const direction = newIndex > previousIndex ? 'left' : 'right';
      this.header.setAttribute('data-nav-direction', direction);
    }

    if (previousIndex >= 0 && previousIndex !== newIndex) {
      this.radios.forEach((radio) =>
        radio.removeAttribute(ASiteHeaderTargetGroupWasActiveAttribute)
      );

      this.header?.setAttribute(
        ASiteHeaderTargetGroupSwitchedAttribute,
        'true'
      );

      const previousRadio = this.radios[previousIndex];
      previousRadio?.setAttribute(
        ASiteHeaderTargetGroupWasActiveAttribute,
        'true'
      );

      if (this.clearWasActiveTimer) {
        window.clearTimeout(this.clearWasActiveTimer);
      }

      this.clearWasActiveTimer = window.setTimeout(() => {
        previousRadio?.removeAttribute(
          ASiteHeaderTargetGroupWasActiveAttribute
        );
      }, ASiteHeaderTargetGroupAnimationDuration);

      if (this.clearTargetGroupSwitchedTimer) {
        window.clearTimeout(this.clearTargetGroupSwitchedTimer);
      }

      this.clearTargetGroupSwitchedTimer = window.setTimeout(() => {
        this.header?.removeAttribute(ASiteHeaderTargetGroupSwitchedAttribute);
      }, ASiteHeaderTargetGroupAnimationDuration);
    }

    this.lastCheckedRadioIndex = newIndex;
  };

  handleScroll = () => {
    const currentScrollY = window.scrollY;

    if (currentScrollY <= ASiteHeaderScrollThreshold) {
      // Above threshold: always show header
      this.header?.classList.remove(ASiteHeaderScrolledUpClass);
    } else if (currentScrollY < this.lastScrollY) {
      // Scrolling up past threshold: show header
      this.header?.classList.remove(ASiteHeaderScrolledUpClass);
    } else if (currentScrollY > this.lastScrollY) {
      // Scrolling down past threshold: hide header
      this.header?.classList.add(ASiteHeaderScrolledUpClass);
    }

    this.lastScrollY = currentScrollY;
  };

  handleResize = () => {
    const isDesktop = window.innerWidth >= ASiteHeaderDesktopBreakpoint;

    if (isDesktop) {
      this.showDesktop();
    } else {
      this.showMobile();
    }
  };

  showDesktop = () => {
    this.navDesktop?.classList.remove(ASiteHeaderHiddenClass);
    this.navMobile?.classList.add(ASiteHeaderHiddenClass);
    this.navMobileOverlay?.removeAttribute('open');
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    this.toggleButton?.setAttribute('aria-label', 'Open menu');
    this.syncNoScrollClass();
  };

  showMobile = () => {
    this.navDesktop?.classList.add(ASiteHeaderHiddenClass);
    this.navMobile?.classList.remove(ASiteHeaderHiddenClass);
    this.syncNoScrollClass();
  };

  toggleMobileNav = () => {
    this.navMobileOverlay?.toggleAttribute('open');
    const isOpen = this.navMobileOverlay?.hasAttribute('open');
    this.toggleButton?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    this.toggleButton?.setAttribute(
      'aria-label',
      isOpen ? 'Sluit menu' : 'Open menu'
    );
    this.syncNoScrollClass();
  };

  setupPopoverObserver = () => {
    if (!this.header) {
      return;
    }

    this.popoverObserver = new MutationObserver((mutations) => {
      const hasOpenAttributeMutation = mutations.some(
        (mutation) => mutation.attributeName === 'open'
      );

      if (hasOpenAttributeMutation) {
        this.syncNoScrollClass();
      }
    });

    this.popoverObserver.observe(this.header, {
      attributes: true,
      attributeFilter: ['open'],
      subtree: true,
    });
  };

  syncNoScrollClass = () => {
    const isMobileMenuOpen = this.navMobileOverlay?.hasAttribute('open');
    const hasOpenPopover = Boolean(
      this.header?.querySelector(ASiteHeaderOpenPopoverSelector)
    );

    document.body.classList.toggle(
      ASiteHeaderNoScrollClass,
      Boolean(isMobileMenuOpen || hasOpenPopover)
    );
  };

  // Temp solution for Coveo searchbox before we switch to custom searchbox
  toggleSearch = () => {
    const searchFoldout = document.querySelector(
      `.${ASiteHeaderCoveoSearchboxClassName}`
    );
    searchFoldout?.classList.toggle(
      `${ASiteHeaderCoveoSearchboxClassName + ASiteHeaderCoveoSearchboxActiveAddition}`
    );
    searchFoldout?.querySelector('input:first-of-type')?.focus();
  };

  openSearch = () => {
    const searchFoldout = document.querySelector(
      `.${ASiteHeaderCoveoSearchboxClassName}`
    );
    searchFoldout?.classList.add(
      `${ASiteHeaderCoveoSearchboxClassName + ASiteHeaderCoveoSearchboxActiveAddition}`
    );
    searchFoldout?.querySelector('input:first-of-type')?.focus();
  };

  closeSearch = () => {
    const searchFoldout = document.querySelector(
      `.${ASiteHeaderCoveoSearchboxClassName}`
    );
    searchFoldout?.classList.remove(
      `${ASiteHeaderCoveoSearchboxClassName + ASiteHeaderCoveoSearchboxActiveAddition}`
    );
  };

  handleSearchboxTabNavigation = (event) => {
    if (event.key !== 'Tab' || event.shiftKey) {
      return;
    }

    const activeElement = document.activeElement;
    if (!activeElement?.matches(ASiteHeaderSearchboxButtonSelector)) {
      return;
    }

    const closeButton = this.getFirstVisibleSearchCloseButton();
    if (!closeButton) {
      return;
    }

    event.preventDefault();
    closeButton.focus();
  };

  getFirstVisibleSearchCloseButton = () => {
    return [...this.searchCloseButtons].find((button) => {
      if (!(button instanceof HTMLElement)) {
        return false;
      }

      if (button.hidden || button.getAttribute('aria-hidden') === 'true') {
        return false;
      }

      return button.offsetParent !== null;
    });
  };
}

// Initiate module
function ASiteHeaderCreateInstance(element) {
  if (!element.siteHeaderClass) {
    element.siteHeaderClass = new ASiteHeader();
    element.siteHeaderClass.init();
  }
}

// Create new instances on DOM mutation
document
  .querySelectorAll(`${ASiteHeaderSelector}`)
  .forEach(ASiteHeaderCreateInstance);

const ASiteHeaderObserver = new MutationObserver(function (mutations) {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      // Node is a site header
      if (node.matches && node.matches(ASiteHeaderSelector)) {
        ASiteHeaderCreateInstance(node);
      }

      // Node contains any site headers
      if (node.querySelectorAll) {
        node
          .querySelectorAll(ASiteHeaderSelector)
          .forEach(ASiteHeaderCreateInstance);
      }
    });
  });
});

ASiteHeaderObserver.observe(document, {
  attributes: false,
  childList: true,
  characterData: false,
  subtree: true,
});
